import React from "react";
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';
import { CheckCircle, Upload, BarChart3, Zap } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  useEffect(() => {
    if (user && !loading) {
      navigate('/launcher/ad-sheets');
    }
  }, [user, loading, navigate]);

  const handleMetaLogin = () => {
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary">
      <header className="border-b border-border bg-background/80 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <h1 className="text-xl font-bold text-foreground">To Better Digital</h1>
          <Button onClick={handleMetaLogin} variant="outline">
            Sign In
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-4xl text-center">
          <h2 className="mb-6 text-5xl font-bold text-foreground">
            Automate Your Meta Ad Creatives
          </h2>
          <p className="mb-8 text-xl text-muted-foreground">
            Upload, manage, and track your Meta advertising creatives with ease.
            Seamless integration with Meta's advertising platform.
          </p>
        </div>

        <div className="mx-auto mt-20 grid max-w-5xl gap-8 md:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Upload className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-card-foreground">
              Upload Creatives
            </h3>
            <p className="text-muted-foreground">
              Easily upload images and videos from Google Drive to your Meta ad accounts
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <BarChart3 className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-card-foreground">
              Track Status
            </h3>
            <p className="text-muted-foreground">
              Monitor upload and processing status in real-time with live updates
            </p>
          </div>

          <div className="rounded-lg border border-border bg-card p-6 text-center">
            <div className="mb-4 flex justify-center">
              <div className="rounded-full bg-primary/10 p-3">
                <Zap className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-card-foreground">
              Multiple Accounts
            </h3>
            <p className="text-muted-foreground">
              Manage creatives across multiple Meta ad accounts from one dashboard
            </p>
          </div>
        </div>
      </main>

      <footer className="border-t border-border bg-background py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>&copy; 2026 To Better Digital</p>
          <p className="mt-2 text-sm">
            <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
            <span className="mx-2">·</span>
            <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
